import { TextAlignment } from "building-types/annotation/annotationTypesEnum";
import {
  sheetPolylineGeometryNew,
  sheetTextGeometryNew,
  type SheetGeometry,
} from "building-types/views/sheetGeometryTypes";
import { ZERO_INCHES } from "common/constants/scaleFactors";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { numAdd, numMul, sheetInchesNew } from "common/utils/nominalNumbers";
import { textAlignmentToXAnchor } from "common/utils/textAlignment";
import { mat3Dot, mat3Translate } from "geom/mat3";
import { polyLine2Transform } from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import { vec2AngleBetweenCW, vec2Normalize, vec2Scale } from "geom/vec2";
import { generateArrowheadGeometry } from "lib/house/annotation/arrowheadGeometry";

interface ElevNoteProps {
  readonly x: SheetInches;
  readonly y: SheetInches;
  readonly text: string;
  readonly leaderVector: Vec2;
  readonly leaderLengthInches: SheetInches;
  readonly leaderHorizontalLengthInches: SheetInches;
}

const elevNoteDefault: ElevNoteProps = {
  x: ZERO_INCHES,
  y: ZERO_INCHES,
  text: "TEST",
  leaderVector: [0, -1],
  leaderLengthInches: sheetInchesNew(0.25),
  leaderHorizontalLengthInches: sheetInchesNew(0.1),
};

export function generateElevNoteSheetGeometries(props: Partial<ElevNoteProps>): SheetGeometry[] {
  const resolvedProps = { ...elevNoteDefault, ...props };
  const {
    leaderVector: [lx, ly],
    leaderHorizontalLengthInches,
  } = resolvedProps;
  const leaderLength = resolvedProps.leaderLengthInches;
  const [vx, vy] = vec2Normalize(lx, ly);
  const [sx, sy] = vec2Scale(vx, vy, leaderLength);
  const rotationRads = vec2AngleBetweenCW(0, 1, vx, vy);

  let leaderHorizontalToLeft = true;
  if (rotationRads >= 0 && rotationRads < Math.PI) {
    leaderHorizontalToLeft = false;
  }

  const horizontalLeaderOffset = numMul(leaderHorizontalLengthInches, leaderHorizontalToLeft ? -1 : 1);
  const offset = sheetInchesNew(0.03);
  const horizontalTextOffset = numAdd(horizontalLeaderOffset, numMul(offset, leaderHorizontalToLeft ? -1 : 1));
  const textAlign = leaderHorizontalToLeft ? TextAlignment.RIGHT : TextAlignment.LEFT;

  // explicit new line handling
  const textLines = resolvedProps.text.split("**");

  const transform = mat3Translate(resolvedProps.x, resolvedProps.y);
  const leaderPolyline = polyLine2Transform(polyLine2New([0, 0, sx, -sy, sx + horizontalLeaderOffset, -sy]), transform);
  const [textX, textY] = mat3Dot(transform, sx + horizontalTextOffset, -sy);

  // Generate arrowhead at the end of the leader
  const arrowheadGeometry = generateArrowheadGeometry({
    position: [resolvedProps.x, resolvedProps.y],
    rotationRads,
    width: 0.06,
    height: 0.07,
  });

  return [
    arrowheadGeometry,
    sheetPolylineGeometryNew({ paths: [leaderPolyline] }),
    sheetTextGeometryNew({
      x: textX,
      y: textY,
      text: textLines.join("\n"),
      placementXAnchor: textAlignmentToXAnchor(textAlign),
    }),
  ];
}
